import { DefaultError, useMutation, useQuery } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  ICreateProviderSftpConnection,
  IProviderSftpConnectionResponse,
  IUpdateProviderSftpConnection,
} from './types';

export const useGetProviderSftpConnection = (
  providerId: string,
  salesChannelId: string
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IProviderSftpConnectionResponse, DefaultError>({
    queryKey: ['sftp-conection', providerId, salesChannelId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Providers/Connection/SFTP`,
        params: { providerId, salesChannelId },
      });

      return responseHandler(response, {
        groupName: 'useGetProviderSftpConnections',
        notFoundPath: `/settings/provider/${providerId}/sftp-connection/create/${salesChannelId}/keys`,
      });
    },
  });
};

export const usePostProviderSftpConnection = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePostProviderSftpConnections'
  );

  return useMutation<
    IProviderSftpConnectionResponse,
    DefaultError,
    ICreateProviderSftpConnection
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-code.create.loading', {
          what: i18next.t('provider.sftp-what'),
        })
      );

      const response = await apiClient({
        method: 'POST',
        url: `Providers/Connection/SFTP`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePostProviderSftpConnections`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.create.success', {
          what: i18next.t('provider.sftp-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutProviderSftpConnection = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'usePutProviderSftpConnection'
  );

  return useMutation<
    IProviderSftpConnectionResponse,
    DefaultError,
    IUpdateProviderSftpConnection
  >({
    mutationFn: async body => {
      loading(
        i18next.t('create.update.loading', {
          what: i18next.t('provider.sftp-what'),
        })
      );

      const response = await apiClient({
        method: 'PUT',
        url: `Providers/Connection/SFTP`,
        body: body,
      });

      return responseHandler(response, {
        groupName: `usePutProviderSftpConnection`,
      });
    },
    onSuccess: data => {
      success(
        i18next.t('service-message.update.success', {
          what: i18next.t('provider.sftp-what'),
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteProviderSftpConnection = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteProviderSftpConnection'
  );

  return useMutation<
    boolean,
    DefaultError,
    { providerId: string; salesChannelId: string }
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: i18next.t('provider.sftp-what'),
        })
      );

      const response = await apiClient({
        method: 'DELETE',
        url: `Providers/Connection/SFTP`,
        body,
      });

      return responseHandler(response, {
        groupName: `useDeleteProviderSftpConnection`,
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.remove.success', {
          what: i18next.t('provider.sftp-what'),
        })
      );
    },
    onError: dismiss,
  });
};
